/**
 * Unsupported-browser fallback page.
 *
 * Rendered by main.tsx when `probeRequiredFeatures()` reports a missing
 * runtime API. Kept in vanilla DOM (no React) on purpose — if the
 * browser is too old to satisfy the probe, it may also be too old to
 * boot React without the white-screen failure mode this page exists to
 * prevent.
 *
 * Strings are inlined English: i18n bundles depend on React. The
 * audience is a user on a stale device who needs one clear instruction.
 */

const BROWSER_FLOOR_DESCRIPTION =
  'Safari 15.6+ or iOS 15.6+, or the latest version of Chrome, Firefox, or Edge.'

export function renderUnsupportedBrowserPage(host: HTMLElement, missing: readonly string[]): void {
  // Clear any existing content (e.g., index.html placeholder).
  while (host.firstChild) host.removeChild(host.firstChild)

  const main = document.createElement('main')
  main.setAttribute('role', 'main')
  main.style.cssText = [
    'max-width: 32rem',
    'margin: 4rem auto',
    'padding: 1.5rem',
    'font-family: system-ui, -apple-system, sans-serif',
    'color: #1a1a1a',
    'line-height: 1.5',
  ].join('; ')

  const h1 = document.createElement('h1')
  h1.textContent = 'Your browser is out of date'
  h1.style.cssText = 'margin: 0 0 1rem; font-size: 1.5rem'
  main.appendChild(h1)

  const lede = document.createElement('p')
  lede.textContent =
    'ReadyRoomOps requires a recent browser to keep your data secure and your photos uploading reliably.'
  main.appendChild(lede)

  const requirement = document.createElement('p')
  const reqLabel = document.createElement('strong')
  reqLabel.textContent = 'Supported browsers: '
  requirement.appendChild(reqLabel)
  requirement.appendChild(document.createTextNode(BROWSER_FLOOR_DESCRIPTION))
  main.appendChild(requirement)

  const action = document.createElement('p')
  action.textContent = 'Please update your browser, then reload this page.'
  main.appendChild(action)

  if (missing.length > 0) {
    const detail = document.createElement('p')
    detail.style.cssText = 'margin-top: 2rem; font-size: 0.75rem; color: #666'
    const detailLabel = document.createElement('strong')
    detailLabel.textContent = 'Missing capability: '
    detail.appendChild(detailLabel)
    // textContent escapes any DOM-special chars in the feature names.
    detail.appendChild(document.createTextNode(missing.join(', ')))
    main.appendChild(detail)
  }

  host.appendChild(main)
}
